import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useMutation, useQuery } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { CustomButton } from '../_common';
import './SplitLoadModal.scss';
import { UPDATE_SPLIT_LOAD } from '../../graphql/client/mutations';
import AddDriverModal from '../AddLoads/AddDriverModal';
import { GET_LOAD_DETAIL, GET_FILTERED_LOAD_MANAGEMENTS, GET_LOADS_BY_JOB_ID } from '../../graphql/client/queries';
import ConfirmDispatchModal from '../AddLoads/DispatchModal';
import { useStoreContext } from '../App/StoreProvider';
import * as Actions from '../../store/actions/addLoadModal';

import { UpdateSplitLoad } from '../../types/schemaTypes';

interface SplitLoadModalProps {
    onClickShow: () => void;
    onClickHide: () => void;
    show: boolean;
    loadId: number;
    lofaId: number;
    proppantName?: string;
    sandPO?: string;
    regionId?: number;
    lofa?: string;
    jobId?: number | null | undefined;
    loadListfilters?: any;
    loadListsearchPhrase?: string;
    driverName?: string;
    refetch: () => void;
}

const SplitLoadModal = ({
    onClickHide,
    show,
    loadId,
    lofaId,
    proppantName,
    sandPO,
    regionId,
    lofa,
    jobId,
    loadListfilters,
    loadListsearchPhrase,
    driverName,
    refetch,
}: SplitLoadModalProps): React.ReactElement => {
    const [showAssignDriverModal, setShowAssignDriverModal] = useState(false);
    const [showDispatchModal, setDispatchModal] = useState(false);
    const [state, dispatch] = useStoreContext();
    const {
        loadManagementList: { filters, searchPhrase },
    } = state;

    const handleClickBack = (): void => {
        setShowAssignDriverModal(false);
        setDispatchModal(true);
    };
    const handleOnClickHide = (): void => {
        setShowAssignDriverModal(false);
    };
    const [errorMessage, setErrorMessage] = useState('');

    const onSplitLoad = (data: UpdateSplitLoad): void => {
        if (data.updateSplitLoad === null) return;
        const updateSplitLoadErrors = data.updateSplitLoad.errors;
        if (updateSplitLoadErrors && updateSplitLoadErrors.length > 0) {
            setErrorMessage(updateSplitLoadErrors[0] || '');
        } else {
            refetch();
            onClickHide();
        }
    };

    const [updateSplitLoad] = useMutation<UpdateSplitLoad>(UPDATE_SPLIT_LOAD, {
        onCompleted: onSplitLoad,
        onError: (e) => {
            if (CombinedGraphQLErrors.is(e)) {
                console.log('GraphQL Error', e.errors);
            } else {
                console.log('Error', e.message);
            }
        },
        refetchQueries: () => {
            return [
                {
                    query: GET_LOAD_DETAIL,
                    variables: { loadId: loadId },
                },
                {
                    query: GET_FILTERED_LOAD_MANAGEMENTS,
                    variables: {
                        input: {
                            regionIds: filters.regionIds,
                            districtIds: filters.districtIds,
                            customerIds: filters.customerIds,
                            jobStatuses: filters.jobStatuses,
                            proppants: filters.proppantIds,
                            statuses: filters.statuses,
                            carriers: filters.carrierIds,
                            drivers: filters.driverIds,
                            lofas: filters.lofaIds,
                            jobIds: filters.jobIds,
                            search: searchPhrase,
                            sortOrder: '',
                        },
                    },
                },
                {
                    query: GET_LOADS_BY_JOB_ID,
                    variables: {
                        input: {
                            jobId: jobId,
                            carriers: loadListfilters.carrierIds,
                            drivers: loadListfilters.driverIds,
                            lofas: loadListfilters.lofaIds,
                            proppants: loadListfilters.proppantIds,
                            statuses: loadListfilters.statuses,
                            search: loadListsearchPhrase,
                        },
                    },
                },
            ];
        },
    });

    const handleOnClick = (data: UpdateSplitLoad): void => {
        if (data.updateSplitLoad === null) return;
        const updateSplitLoadErrors = data.updateSplitLoad.errors;
        if (updateSplitLoadErrors && updateSplitLoadErrors.length > 0) {
            setErrorMessage(updateSplitLoadErrors[0] || '');
        } else {
            dispatch(Actions.setCurrentLoadId(loadId));
            dispatch(Actions.setCreatedLoadIds([loadId]));
            setShowAssignDriverModal(true);
        }
    };

    const [updateSplitLoadAndAssignDriver] = useMutation<UpdateSplitLoad>(UPDATE_SPLIT_LOAD, {
        onCompleted: handleOnClick,
        onError: (e) => {
            if (CombinedGraphQLErrors.is(e)) {
                console.log('GraphQL Error', e.errors);
            } else {
                console.log('Error', e.message);
            }
        },
    });

    const handleUpdateSplitLoadAndAssignDriver = (): void => {
        updateSplitLoadAndAssignDriver({
            variables: { loadId: loadId, isSplit: true },
        });
    };

    const handleClickHide = (): void => {
        setDispatchModal(false);
        onClickHide();
        refetch();
    };

    const region = {
        id: regionId || null,
        regionName: '',
        extSystemName: '',
        extSystemReference: '',
        regionCode: '',
    };

    const skipAndSplit = (): void => {
        updateSplitLoad({
            variables: { loadId: loadId, isSplit: true },
        });
    };

    const onClickOK = (): void => {
        window.location.reload();
    };

    return (
        <Modal show={show} onHide={onClickHide}>
            <Modal.Header className="SplitLoadModal-Header" closeButton>
                <Modal.Title className="SplitLoadModal-Header-Container">Split Loads</Modal.Title>
            </Modal.Header>
            {errorMessage !== '' ? (
                <Modal.Body className="SplitModal-Body">
                    <div className="SplitModal-Body-Body">{errorMessage}</div>
                </Modal.Body>
            ) : (
                <Modal.Body className="SplitModal-Body">
                    <div className="SplitLoadModal-Body-Header">Load #{loadId} has been split.</div>
                    <div className="SplitModal-Body-Body">Do you want to assign a driver to a split load?</div>
                </Modal.Body>
            )}

            {errorMessage !== '' ? (
                <Modal.Footer className="SplitLoadModal-Footer">
                    <CustomButton title="OK" isBordered={true} onClick={onClickOK}></CustomButton>
                </Modal.Footer>
            ) : (
                <Modal.Footer className="SplitLoadModal-Footer">
                    <CustomButton title="Skip" isBordered={true} onClick={skipAndSplit}></CustomButton>
                    <CustomButton
                        title="Find Driver"
                        isBordered={false}
                        onClick={handleUpdateSplitLoadAndAssignDriver}
                    ></CustomButton>
                    {showAssignDriverModal && (
                        <AddDriverModal
                            jobId={jobId}
                            show={showAssignDriverModal}
                            onClickBack={handleClickBack}
                            onClickHide={handleOnClickHide}
                            region={region}
                            load={String(loadId)}
                            lofaId={lofaId || 0}
                            proppantName={proppantName}
                            lofa={lofa || ''}
                            isFromLoads={true}
                            isSplitLoad={true}
                            sandPO={sandPO}
                            driverName={driverName}
                        />
                    )}
                    <ConfirmDispatchModal
                        isFromLoads={true}
                        isSplitLoad={true}
                        show={showDispatchModal}
                        onClickHide={handleClickHide}
                    />
                </Modal.Footer>
            )}
        </Modal>
    );
};

export default SplitLoadModal;

